import { Component, type ErrorInfo, type ReactNode } from "react";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Top-level error boundary that catches unhandled React render errors
 * and displays a recovery UI instead of a blank white page.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, info.componentStack);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleLogin = () => {
    // Clear the MSAL cache (now in localStorage, shared across tabs) and redirect to login
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/login";
  };

  override render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const message = this.state.error?.message ?? "An unexpected error occurred.";
    const isAuthError =
      message.includes("timed_out") ||
      message.includes("interaction_required") ||
      message.includes("No active account");

    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>
              {isAuthError ? "Session Expired" : "Something went wrong"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {isAuthError
                ? "Your session has expired or could not be refreshed. Please sign in again to continue."
                : "The application encountered an unexpected error. You can try reloading the page."}
            </p>
            {!isAuthError && (
              <pre className="mt-4 max-h-32 overflow-auto rounded-md bg-muted p-3 text-xs text-muted-foreground">
                {message}
              </pre>
            )}
          </CardContent>
          <CardFooter className="gap-2">
            {isAuthError ? (
              <Button onClick={this.handleLogin}>Sign in</Button>
            ) : (
              <>
                <Button onClick={this.handleReload}>Reload page</Button>
                <Button variant="outline" onClick={this.handleLogin}>
                  Sign in again
                </Button>
              </>
            )}
          </CardFooter>
        </Card>
      </div>
    );
  }
}
